import { useCallback, useSyncExternalStore } from 'react';

/**
 * App-wide light/dark color theme preference.
 *
 * Only solink-ui surfaces and design tokens react to the `.dark` class today
 * (see packages/solink-ui/src/globals.css). Legacy SASS / styled-components
 * pages do not support dark yet, so this is an in-progress theme switch.
 *
 * The class goes on `document.documentElement` so it also reaches Radix
 * overlays (dropdowns, tooltips, popovers) that portal to `document.body`.
 *
 * Preference is `system | light | dark`. `system` follows the OS via
 * `prefers-color-scheme`. `.dark` is applied only while the Aspen shell is
 * active so classic-nav users (and shared Agents/SDM on this origin) never get
 * dark chrome with no toggle.
 */
export type ColorThemePreference = 'system' | 'light' | 'dark';
/** @deprecated Use ColorThemePreference — kept as an alias for callers. */
export type ColorTheme = ColorThemePreference;

/**
 * Survives sign-out. `StorageAPI.clearNonPersistent()` deletes every key that
 * does not start with `PERSISTENT_`.
 */
const STORAGE_KEY = 'PERSISTENT_vd-color-theme';
/**
 * Previous key. Sign-out deletes it. Copy a valid value to `STORAGE_KEY`.
 * Do not delete this key here. A tab that still runs the previous bundle
 * treats that delete as "system".
 */
const LEGACY_STORAGE_KEY = 'vd-color-theme';
const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

/**
 * Last-known Aspen shell, for the pre-paint script and `initColorTheme`.
 *
 * The theme toggle lives only on the Aspen nav rail. Applying `.dark` without
 * that shell would leave a user with no way to switch back if `aspenWeb` is
 * off. The theme choice stays in localStorage. Do not wipe that choice.
 *
 * Temporary: remove `ASPEN_SHELL_STORAGE_KEY`, `setColorThemeAspenShell`, the
 * `aspenShellActive` gate in `applyTheme`, and the matching `index.html`
 * check once everyone is on Aspen.
 */
const ASPEN_SHELL_STORAGE_KEY = 'vd-color-theme-aspen-shell';

const THEME_CYCLE: ColorThemePreference[] = ['system', 'light', 'dark'];

function isColorThemePreference(value: string | null): value is ColorThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredPreference(): ColorThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isColorThemePreference(stored)) return stored;

    const legacy = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (isColorThemePreference(legacy)) {
      try {
        localStorage.setItem(STORAGE_KEY, legacy);
      } catch {
        // Keep the in-memory value when the new key cannot be written.
      }
      return legacy;
    }
    // Missing / unknown → follow the OS (same as an explicit 'system').
    return 'system';
  } catch {
    // localStorage can be blocked (private mode / disabled storage).
    return 'system';
  }
}

/**
 * Only an explicit `'1'` means Aspen was on last session. Missing or `'0'`
 * suppresses pre-paint `.dark` so classic users on a dark OS do not flash
 * (and so Agents/SDM on this origin never inherit dark with no toggle).
 */
function readAspenShellHint(): boolean {
  try {
    return localStorage.getItem(ASPEN_SHELL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function getSystemPrefersDark(): boolean {
  try {
    return window.matchMedia(COLOR_SCHEME_QUERY).matches;
  } catch {
    return false;
  }
}

function resolveIsDark(preference: ColorThemePreference): boolean {
  if (preference === 'dark') return true;
  if (preference === 'light') return false;
  return getSystemPrefersDark();
}

// Module-level store so every consumer shares one preference and stays in sync.
const listeners = new Set<() => void>();
let currentPreference: ColorThemePreference = readStoredPreference();
let aspenShellActive = readAspenShellHint();
let mediaQueryList: MediaQueryList | null = null;
/** Bumps when resolved dark (OS / Aspen) changes without the stored preference changing. */
let storeRevision = 0;

function notifyListeners(): void {
  storeRevision += 1;
  listeners.forEach((listener) => listener());
}

function applyTheme(): void {
  try {
    // Temporary Aspen gate — remove once everyone is on Aspen (see ASPEN_SHELL_STORAGE_KEY).
    const dark = aspenShellActive && resolveIsDark(currentPreference);
    document.documentElement.classList.toggle('dark', dark);
  } catch {
    // No DOM (non-browser environment) — nothing to apply.
  }
}

function handleSystemColorSchemeChange(): void {
  if (currentPreference !== 'system') return;
  applyTheme();
  notifyListeners();
}

function syncSystemMediaListener(): void {
  const shouldListen = aspenShellActive && currentPreference === 'system';
  try {
    if (shouldListen) {
      if (!mediaQueryList) {
        mediaQueryList = window.matchMedia(COLOR_SCHEME_QUERY);
        mediaQueryList.addEventListener('change', handleSystemColorSchemeChange);
      }
    } else if (mediaQueryList) {
      mediaQueryList.removeEventListener('change', handleSystemColorSchemeChange);
      mediaQueryList = null;
    }
  } catch {
    // No window / matchMedia — system follow is unavailable.
  }
}

/**
 * Apply the stored theme to the DOM.
 *
 * Call this once from the app bootstrap (index.tsx). An inline script in
 * index.html already sets the class before first paint to avoid a flash;
 * this keeps the class correct when that script does not run (for example in
 * tests). Importing this module has no side effect on the DOM.
 *
 * `.dark` is applied only while the Aspen shell is active (see
 * `setColorThemeAspenShell`). Temporary: drop that gate after full Aspen migration.
 */
export function initColorTheme(): void {
  applyTheme();
  syncSystemMediaListener();
}

/**
 * Tell the theme store whether the Aspen nav shell is showing.
 *
 * Why: the toggle is only in that rail. Keep the stored theme choice, but
 * strip `.dark` when Aspen is off so the user is not stuck dark with no
 * control. When Aspen returns, the stored value is reapplied.
 *
 * Temporary: delete this function and its `NavigationFrame` call once everyone
 * is on Aspen. The class can then always follow the stored theme choice.
 */
export function setColorThemeAspenShell(enabled: boolean): void {
  if (enabled === aspenShellActive) return;
  aspenShellActive = enabled;
  try {
    localStorage.setItem(ASPEN_SHELL_STORAGE_KEY, enabled ? '1' : '0');
  } catch {
    // Persist is best-effort — still apply/strip the class in this tab.
  }
  applyTheme();
  syncSystemMediaListener();
  notifyListeners();
}

function setPreference(preference: ColorThemePreference): void {
  if (preference === currentPreference) return;
  currentPreference = preference;
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Persist is best-effort — keep the in-memory value even when storage fails.
  }
  applyTheme();
  syncSystemMediaListener();
  notifyListeners();
}

function cyclePreference(): void {
  const index = THEME_CYCLE.indexOf(currentPreference);
  const next = THEME_CYCLE[(index + 1) % THEME_CYCLE.length] ?? 'system';
  setPreference(next);
}

// Mirror a theme change made in another tab. The `storage` event fires only in
// tabs other than the one that wrote the value, so this never double-applies.
function handleStorageEvent(event: StorageEvent): void {
  const raw = event.newValue;
  if (event.key === LEGACY_STORAGE_KEY) {
    // A delete of the old key is not a theme change. Sign-out removes it.
    // Ignore that event so it cannot reset the saved choice.
    if (!isColorThemePreference(raw)) return;
    try {
      localStorage.setItem(STORAGE_KEY, raw);
    } catch {
      // The in-memory value still applies for this page load.
    }
  } else if (event.key !== STORAGE_KEY) {
    return;
  }

  const next: ColorThemePreference = isColorThemePreference(raw) ? raw : 'system';
  if (next === currentPreference) return;
  currentPreference = next;
  applyTheme();
  syncSystemMediaListener();
  notifyListeners();
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0) {
    try {
      window.addEventListener('storage', handleStorageEvent);
    } catch {
      // No window (non-browser environment) — cross-tab sync is unavailable.
    }
    syncSystemMediaListener();
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      try {
        window.removeEventListener('storage', handleStorageEvent);
      } catch {
        // No window — nothing to remove.
      }
      // Intentional: the matchMedia 'change' listener is NOT torn down by subscriber
      // count. initColorTheme() sets it up at bootstrap so the DOM `.dark` class keeps
      // following the OS even with no React consumer mounted. syncSystemMediaListener
      // removes it only when the OS-follow condition ends (Aspen off, or preference not
      // 'system'), never merely because the last hook consumer unmounted.
      syncSystemMediaListener();
    }
  };
}

export function useColorTheme(): {
  preference: ColorThemePreference;
  theme: ColorThemePreference;
  isDark: boolean;
  setPreference: (preference: ColorThemePreference) => void;
  cycle: () => void;
  /** @deprecated Prefer `cycle` / `setPreference`. */
  toggle: () => void;
} {
  const preference = useSyncExternalStore(
    subscribe,
    () => currentPreference,
    () => 'system' as ColorThemePreference,
  );
  // Re-render when OS / Aspen resolution changes while preference stays `system`.
  useSyncExternalStore(
    subscribe,
    () => storeRevision,
    () => 0,
  );

  const setPreferenceCb = useCallback((next: ColorThemePreference) => {
    setPreference(next);
  }, []);

  const cycle = useCallback(() => {
    cyclePreference();
  }, []);

  return {
    preference,
    theme: preference,
    isDark: resolveIsDark(preference),
    setPreference: setPreferenceCb,
    cycle,
    toggle: cycle,
  };
}
